import { useState, useEffect } from "react";
import { T } from "@/components/T";
import { createPortal } from "react-dom";
import { Clock, AlertTriangle, Star, CalendarDays, Hourglass, Layers, X, ChevronRight, ChevronDown, MapPin, Sparkles, Sun as SunLucide, Flame, UserPlus, Scroll } from "lucide-react";
import { NA, fmtTime, fmtSpan, qtone } from "@/lib/premiumFormat";
import { SunIcon, MoonIcon, SunriseIcon, SunsetIcon, MoonriseIcon, MoonsetIcon } from "@/components/CelestialIcons";
import PanchangShareImage from "@/components/PanchangShareImage";
import AuspiciousWindows from "@/components/AuspiciousWindows";
import MoonPhase from "@/components/MoonPhase";
import CivilTithiNote from "@/components/CivilTithiNote";
import { PL, moonInNakshatra, moonPhaseLine } from "@/components/PL";
import { useLang } from "@/context/LanguageContext";

const shareBtnCls = "!text-white/80 hover:!text-amber-200";

// Derive the Moon's phase from the engine's tithi (num + paksha) — the engine
// returns no explicit illumination, so this is the authoritative reckoning:
// Shukla Pratipada→Purnima is waxing (0→full), Krishna is waning (full→new).
function moonPhaseInfo(tithi) {
  if (!tithi) return null;
  const num = tithi.num ?? tithi.number;
  if (num == null) return null;
  const paksha = String(tithi.paksha || "").toLowerCase();
  const waxing = paksha.includes("shukla") || paksha.includes("shukl") || paksha.includes("bright");
  // The engine numbers Krishna tithis 16-30 (Amavasya = 30); only shift 1-15 numbering.
  const monthIndex = (waxing || num > 15 ? num : 15 + num) % 30; // 0=new .. 15=full .. 30≡new
  const phase = Math.round((monthIndex / 30) * 8) % 8; // 0 new .. 4 full (MoonPhase glyph)
  const illum = Math.round(((1 - Math.cos((2 * Math.PI * monthIndex) / 30)) / 2) * 100);
  let name;
  if (illum <= 2) name = "New Moon · Amāvāsyā";
  else if (illum >= 98) name = "Full Moon · Pūrṇimā";
  else if (waxing) name = illum < 45 ? "Waxing Crescent" : illum > 55 ? "Waxing Gibbous" : "First Quarter";
  else name = illum > 55 ? "Waning Gibbous" : illum < 45 ? "Waning Crescent" : "Last Quarter";
  return { phase, illum, waxing, name };
}

// Karmakala — English translations + the five scripturally-prominent divisions
// (highlighted). The engine returns 8 proportional day/night divisions.
const KARMAKALA_EN = {
  arunodaya: "Pre-dawn",
  pratah: "Early Morning",
  sangava: "Forenoon",
  madhyahna: "Midday",
  aparahna: "Afternoon",
  sayahna: "Evening Twilight",
  pradosha: "Early Night",
  nishitha: "Midnight",
};
const KARMAKALA_ORDER = ["arunodaya", "pratah", "sangava", "madhyahna", "aparahna", "sayahna", "pradosha", "nishitha"];
const KARMAKALA_PROMINENT = new Set(["madhyahna", "aparahna", "pradosha", "nishitha", "arunodaya"]);

// Format an end/start time and prepend the weekday when it falls on a
// different calendar day than the reference (the day's sunrise) — e.g.
// "Mon 10:13 PM" instead of a bare "10:13 PM" that looks already past.
function fmtEndDay(unix, ref, tz) {
  const t = fmtTime(unix, tz);
  try {
    const dayOf = (u) => new Intl.DateTimeFormat("en-GB", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(u * 1000));
    if (ref != null && dayOf(unix) !== dayOf(ref)) {
      const wd = new Intl.DateTimeFormat("en-GB", { timeZone: tz, weekday: "short" }).format(new Date(unix * 1000));
      return `${wd} ${t}`;
    }
  } catch { /* fall through */ }
  return t;
}


const DOT = { good: "bg-emerald-400", neutral: "bg-amber-400", bad: "bg-rose-400" };

const WINDOW_INFO = {
  "Brahma Muhurta": "The pre-dawn hour (~96 min before sunrise) — the most sattvic time for meditation, japa and study.",
  "Abhijit Muhurta": "The victorious midday muhurta — favourable for important beginnings and sankalpa.",
  "Rahu Kalam": "An inauspicious daily period — avoid starting new or auspicious ventures.",
  "Yamaganda": "An inauspicious period ruled by Yama — avoid auspicious activities.",
  "Gulika Kalam": "Generally inauspicious — keep to routine tasks; avoid new undertakings.",
};
const WINDOW_SHORT = {
  "Brahma Muhurta": "Best for meditation & japa",
  "Abhijit Muhurta": "Victorious midday window",
  "Rahu Kalam": "Avoid new beginnings",
  "Yamaganda": "Avoid auspicious work",
  "Gulika Kalam": "Keep to routine tasks",
};

const Card = ({ children, className = "" }) => (
  <div className={`rounded-[1.5rem] border border-white/[0.08] bg-white/[0.03] p-5 ${className}`}>{children}</div>
);
const Label = ({ children }) => (
  <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-amber-300/70">{children}</p>
);

// ── Tarabalam & Chandrabalam card (H) — one shared component ──────────────────
// Reads tara.quality & chandrabala (good / chandrashtama) verbatim from the
// engine. Pill: good=+1, mixed=0, bad/caution=-1 → total ≥2 Good, ≤-2 Caution,
// else Mixed. Windows prepend a short date when they fall on another day.
function TaraChandraCard({ tara, tz, refDate }) {
  const [expT, setExpT] = useState(false);
  const [expC, setExpC] = useState(false);
  const t = tara?.tara;
  const c = tara?.chandrabala;
  if (!t && !c) return null;
  const taraTone = t ? qtone(t.quality) : null; // good / neutral / bad
  const taraWord = taraTone === "good" ? "good" : taraTone === "bad" ? "bad" : "mixed";
  const chandraState = c ? (c.chandrashtama ? "chandrashtama" : c.good ? "good" : "caution") : null;
  const score = (taraTone === "good" ? 1 : taraTone === "bad" ? -1 : 0)
    + (c ? (c.chandrashtama ? -1 : c.good ? 1 : -1) : 0);
  const pill = score >= 2
    ? { label: "Good", cls: "border border-emerald-400/40 bg-emerald-400/15 text-emerald-300" }
    : score <= -2
      ? { label: "Caution", cls: "border border-rose-400/40 bg-rose-400/15 text-rose-300" }
      : { label: "Mixed", cls: "border border-amber-400/40 bg-amber-400/15 text-amber-200" };
  const fmtDated = (unix) => {
    const tm = fmtTime(unix, tz);
    try {
      const dayOf = (u) => new Intl.DateTimeFormat("en-GB", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(u * 1000));
      if (refDate != null && dayOf(unix) !== dayOf(refDate)) {
        const md = new Intl.DateTimeFormat("en-US", { timeZone: tz, month: "short", day: "numeric" }).format(new Date(unix * 1000));
        return `${md}, ${tm}`;
      }
    } catch { /* fall through */ }
    return tm;
  };
  const win = (s, e) => (s != null && e != null) ? `${fmtDated(s)} – ${fmtDated(e)}` : null;
  const taraWin = t && t.start != null ? win(t.start, t.end) : null;
  const wordCls = (w) => (w === "good" ? "text-emerald-300" : w === "caution" || w === "bad" || w === "chandrashtama" ? "text-rose-300" : "text-amber-200");
  return (
    <div data-testid="tara-chandra-card" className="rounded-[1.5rem] border border-white/[0.08] bg-white/[0.02] p-4 sm:p-5">
      <div className="flex items-center justify-between gap-3">
        <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-amber-300/80"><T>Tarabalam & Chandrabalam</T></p>
        <span data-testid="tara-chandra-pill" className={`shrink-0 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${pill.cls}`}>{pill.label}</span>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3">
        {t && (
          <button type="button" onClick={() => setExpT((v) => !v)} data-testid="tara-sub" className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 text-left transition-colors hover:border-white/20">
            <p className="text-[10px] uppercase tracking-[0.16em] text-white/40"><T>Tarabalam</T></p>
            <p className="mt-1 font-serif-latin text-xl leading-tight text-white">{NA(t.name)}<span className={`text-sm ${wordCls(taraWord)}`}> · {taraWord}</span></p>
            {taraWin && <p className="mt-1.5 text-xs text-white/55"><T>Window ·</T> <span className="dh-num">{taraWin}</span></p>}
            {expT && <p className="mt-1 text-xs text-white/40"><T>Tara #</T>{NA(t.number)} <T>· count</T> {NA(t.count)}</p>}
          </button>
        )}
        {c && (
          <button type="button" onClick={() => setExpC((v) => !v)} data-testid="chandra-sub" className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 text-left transition-colors hover:border-white/20">
            <p className="text-[10px] uppercase tracking-[0.16em] text-white/40"><T>Chandrabalam</T></p>
            <p className="mt-1 font-serif-latin text-xl leading-tight text-white"><T>Moon · house</T> {NA(c.house)}<span className={`text-sm ${wordCls(chandraState)}`}> · {chandraState === "chandrashtama" ? "Chandrashtama" : chandraState}</span></p>
            {expC && <p className="mt-1 text-xs text-white/40"><T>Birth Moon</T> {NA(tara.birth?.moon_rashi)} <T>→ Now</T> {NA(tara.at?.moon_rashi)}{c.start != null ? ` · ${win(c.start, c.end)}` : ""}</p>}
          </button>
        )}
      </div>
    </div>
  );
}

// Split a slot list into day/night. Horas carry an explicit `phase` ("day"/"night");
// Choghadiya slots are returned day-first, so when no phase/period marker exists we
// split at the midpoint (8 daytime + 8 night, per tradition). Original indices are
// preserved so each card keeps a stable, unique data-testid.
const isDayNight = (v) => ["day", "night"].includes(String(v || "").toLowerCase());
const splitDayNight = (arr) => {
  const indexed = arr.map((x, i) => ({ x, i }));
  const marked = indexed.some(({ x }) => isDayNight(x.phase ?? x.period));
  if (marked) {
    const night = ({ x }) => String(x.phase ?? x.period ?? "").toLowerCase() === "night";
    return { day: indexed.filter((e) => !night(e)), night: indexed.filter(night) };
  }
  const mid = Math.ceil(indexed.length / 2);
  return { day: indexed.slice(0, mid), night: indexed.slice(mid) };
};

// Bento tile for a Choghadiya / Hora slot. The current running slot glows.
const QLABEL = { good: "Good", bad: "Avoid", neutral: "Neutral" };
// Verbatim engine quality text, title-cased — e.g. "Favourable", "Caution", "Selective".
const qVerbatim = (q) => String(q || "").trim().replace(/(^|\s)\S/g, (c) => c.toUpperCase());
const TimeCard = ({ testid, tone, isCurrent, title, sub, span, nowLabel = "Now", qlabel }) => (
  <div data-testid={testid} className="ptile" data-on={isCurrent ? "true" : "false"}>
    <div className="flex items-start justify-between gap-2">
      <span className="ptile-name">{title}</span>
      {isCurrent && <span className="ptile-now shrink-0">{nowLabel}</span>}
    </div>
    <span className="ptile-time">{span}</span>
    <div className="ptile-foot">
      <span className="ptile-q" data-tone={tone}>{qlabel || QLABEL[tone] || "Neutral"}</span>
      {sub && <span className="ptile-sub">{sub}</span>}
    </div>
  </div>
);

const DayNightBlock = ({ testid, label, Icon, items, render }) => {
  if (!items.length) return null;
  return (
    <div data-testid={testid}>
      <p className="mb-2.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-amber-300/70">
        <Icon className="h-3.5 w-3.5" /> {label}
      </p>
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
        {items.map(({ x, i }) => render(x, i))}
      </div>
    </div>
  );
};

// Bottom sheet showing the key info for a tapped label/window/limb.
function DetailSheet({ detail, onClose }) {
  if (!detail) return null;
  return createPortal(
    <div className="fixed inset-0 z-[9997] flex items-end justify-center bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="detail-sheet">
      <div className="flex max-h-[75vh] w-full max-w-lg flex-col rounded-t-3xl border-t border-white/15 bg-[#121829] shadow-2xl pb-[env(safe-area-inset-bottom)]" onClick={(e) => e.stopPropagation()}>
        {/* grab handle */}
        <div className="flex shrink-0 justify-center pt-3" data-testid="detail-handle"><span className="h-1.5 w-12 rounded-full bg-white/25" /></div>
        {/* header */}
        <div className="flex shrink-0 items-start justify-between gap-3 px-6 pb-3 pt-3">
          <div className="flex items-center gap-2">
            {detail.tone && <span className={`h-2.5 w-2.5 rounded-full ${DOT[detail.tone]}`} />}
            <h3 className="font-serif-latin text-xl text-white">{detail.title}</h3>
          </div>
          <button onClick={onClose} data-testid="detail-close" aria-label="Close" className="shrink-0 rounded-full p-1 text-white/60 hover:bg-white/10 hover:text-white"><X className="h-5 w-5" /></button>
        </div>
        {/* scrollable body */}
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 pb-8">
          {detail.subtitle && <p className="mb-3 text-sm text-amber-200/90">{detail.subtitle}</p>}
          <div className="divide-y divide-white/10">
            {detail.rows.filter((r) => r.value != null && r.value !== "").map((r, i) => (
              <div key={i} className="flex items-start justify-between gap-6 py-2.5">
                <span className="text-xs uppercase tracking-wide text-white/50">{r.label}</span>
                <span className="text-right text-sm text-white/90">{r.value}</span>
              </div>
            ))}
          </div>
          {detail.note && <p className="mt-4 rounded-xl bg-white/5 p-3 text-sm leading-relaxed text-white/70">{detail.note}</p>}
        </div>
      </div>
    </div>,
    document.body
  );
}

// Dedicated Moon card — mirrors the Sun hero (gradient container + moon-path
// arc). Values are formatted/positioned verbatim from the engine (moonrise/
// moonset/moon rashi); above/below-horizon is a tz-safe time comparison only.
function MoonCard({ panchang, astro, tz, selectedSec = null, pt = (s) => s }) {
  const { lang } = useLang();
  if (astro?.moonrise == null && astro?.moonset == null) return null;
  const ph = moonPhaseInfo(panchang?.tithi);
  const moonNak = panchang?.nakshatra?.name;
  const sub = [moonNak ? moonInNakshatra(pt(NA(moonNak)), lang) : null, ph ? moonPhaseLine(ph.name, ph.illum, lang) : null].filter(Boolean).join(" · ");
  const paksha = panchang?.tithi?.paksha;
  return (
    <div className="relative overflow-hidden rounded-[1.75rem] border border-[#8FB7E8]/15 bg-gradient-to-b from-[#8FB7E8]/[0.06] to-transparent p-5" data-testid="moon-card">
      <div className="flex items-center justify-between gap-3">
        <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#9FC0EC]/80"><T>Moon</T></p>
        {paksha && <span data-testid="moon-paksha" className="shrink-0 rounded-full border border-amber-400/30 bg-amber-400/10 px-3 py-1 text-xs font-medium text-amber-200">{pt(paksha)} <T>Paksha</T></span>}
      </div>
      <div className="mt-2 flex items-center gap-3">
        <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#8FB7E8]/12 ring-1 ring-[#8FB7E8]/25" data-testid="moon-phase-glyph">
          {ph ? <MoonPhase phase={ph.phase} size={28} /> : <MoonIcon className="celestial-pulse h-6 w-6 text-[#C9DAF2]" />}
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="font-serif-latin text-2xl leading-tight text-white [overflow-wrap:normal]">{pt(NA(panchang?.rashi?.moon))}</h2>
          {sub && <p className="mt-0.5 text-xs leading-snug text-[#9FC0EC]/70" data-testid="moon-phase-label">{sub}</p>}
        </div>
      </div>
      <MoonArc moonrise={astro.moonrise} moonset={astro.moonset} tz={tz} selectedSec={selectedSec} phase={ph?.phase} />
    </div>
  );
}

function LimbCard({ label, limb, tz, refDate, Icon, onOpen, pt = (s) => s }) {
  if (!limb) return <div data-testid={`limb-${label}`} className="pd-surf2 rounded-2xl p-4"><p className="pd-ink3 text-[10px] font-medium uppercase tracking-wide">{label}</p><p className="pd-ink3 mt-1 text-sm"><T>Not available</T></p></div>;
  const endStr = limb.end != null ? fmtEndDay(limb.end, refDate, tz) : null;
  const startStr = limb.start != null ? fmtEndDay(limb.start, refDate, tz) : null;
  const rows = [
    { label: "Name", value: pt(NA(limb.name)) },
    { label: "Paksha", value: pt(limb.paksha) },
    { label: "Devata", value: limb.devata },
    { label: "Pada", value: limb.pada },
    { label: "Starts", value: startStr },
    { label: "Ends", value: endStr },
    { label: "Next", value: pt(limb.next_name) },
  ];
  return (
    <button type="button" data-testid={`limb-${label}`}
      onClick={() => onOpen({ title: `${label} · ${pt(NA(limb.name))}`, rows })}
      className="pd-surf2 group flex w-full flex-col rounded-2xl p-4 text-left transition-transform hover:-translate-y-0.5">
      <span className="mb-2.5 inline-flex h-9 w-9 items-center justify-center rounded-xl bg-amber-400/10 ring-1 ring-amber-400/20">
        {Icon && <Icon className="h-[18px] w-[18px] text-amber-300" />}
      </span>
      <p className="pd-ink3 text-[10px] font-medium uppercase tracking-wide">{label}</p>
      <p className="pd-ink mt-1 font-serif-latin text-xl leading-tight">{pt(NA(limb.name))}</p>
      {endStr && <p className="pd-ink2 mt-1.5 text-[13px]"><T>till</T> {endStr}</p>}
      {limb.pada && <p className="pd-ink3 text-xs"><T>Pada</T> {limb.pada}</p>}
    </button>
  );
}

// Single-open accordion section. Only one section is open across the page.
function AccSection({ title, sub, Icon, open, onToggle, children, testid }) {
  return (
    <div className="pacc" data-open={open ? "true" : "false"} data-testid={testid}>
      <button type="button" className="pacc-head" onClick={onToggle} aria-expanded={open} data-testid={`${testid}-head`}>
        <span className="pacc-ico"><Icon className="h-5 w-5" /></span>
        <span className="min-w-0 flex-1">
          <span className="pacc-title block">{title}</span>
          {sub && <span className="pacc-sub block truncate">{sub}</span>}
        </span>
        <ChevronDown className="pacc-chev" />
      </button>
      {open && <div className="pacc-body" data-testid={`${testid}-body`}>{children}</div>}
    </div>
  );
}

export default function PremiumPanchang({ panchang, tara, choghadiya, profile, place = "Current location", dateLabel, mode = "daily", festivals = null, onAddBirth, selectedSec = null, pt = (s) => s }) {
  const [detail, setDetail] = useState(null);
  const [openChog, setOpenChog] = useState(mode === "birth");
  const [openHora, setOpenHora] = useState(mode === "birth");
  const secKey = `plux_sec_${mode}`;
  const [openSec, setOpenSec] = useState(() => { try { return sessionStorage.getItem(secKey) ?? (mode === "birth" ? "details" : "today"); } catch { return mode === "birth" ? "details" : "today"; } });
  useEffect(() => { try { sessionStorage.setItem(secKey, openSec || ""); } catch { /* ignore */ } }, [openSec, secKey]);
  const toggleSec = (id) => setOpenSec((cur) => (cur === id ? null : id));
  const [now, setNow] = useState(() => Date.now() / 1000);
  useEffect(() => {
    if (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const id = setInterval(() => setNow(Date.now() / 1000), 1000);
    return () => clearInterval(id);
  }, []);
  if (!panchang) return null;
  const tz = panchang.tz_str || "Asia/Kolkata";
  const sv = panchang.samvat || {};
  const astro = panchang.astro || {};
  const w = panchang.windows || {};
  const ct = choghadiya?.tz || tz;
  const paksha = panchang.tithi?.paksha;
  const isBirth = mode === "birth";
  const curHora = (panchang.horas || []).find((h) => h.isCurrent);
  const curChog = (choghadiya?.slots || []).find((s) => s.isCurrent);
  // Birth reference moment — the engine marks the slot active AT BIRTH, so its
  // midpoint is a safe "now" for judging birth-time windows (never the clock).
  const birthNow = isBirth
    ? (curChog && curChog.start != null ? (curChog.start + curChog.end) / 2
      : curHora && curHora.start != null ? (curHora.start + curHora.end) / 2
      : astro.sunrise)
    : null;
  const span = (win) => (win && win.start != null ? fmtSpan(win.start, win.end, tz) : "—");

  // ── Personal layer (display only — reads existing engine values) ──
  const prof = profile && profile.dob ? profile : null;
  const _rawName = (prof?.name || "").trim();
  const dispName = _rawName && _rawName.toLowerCase() !== "me" ? _rawName : "";
  const bestWin = w.abhijit && w.abhijit.start != null ? fmtSpan(w.abhijit.start, w.abhijit.end, tz) : (w.brahma && w.brahma.start != null ? fmtSpan(w.brahma.start, w.brahma.end, tz) : null);
  const bestWinName = w.abhijit && w.abhijit.start != null ? "Abhijit Muhurta" : "Brahma Muhurta";

  const fmtCountdown = (end) => {
    const s = Math.max(0, Math.floor(end - now));
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
    return h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m ${String(sec).padStart(2, "0")}s` : `${sec}s`;
  };

  // Map the engine payload into the shared image's data shape (branded premium card).
  const shareData = {
    date: dateLabel,
    place,
    vara: { en: panchang.vara?.name },
    tithi: { en: panchang.tithi?.name },
    paksha: { en: paksha },
    nakshatra: { en: panchang.nakshatra?.name },
    yoga: { en: panchang.yoga?.name },
    karana: { en: panchang.karana?.name },
    sunrise: fmtTime(astro.sunrise, tz),
    sunset: fmtTime(astro.sunset, tz),
    muhurta: {
      brahma: span(w.brahma), abhijit: span(w.abhijit),
      rahu_kalam: span(w.rahu), yamaganda: span(w.yama), gulika_kalam: span(w.gulika),
    },
    choghadiya: { day: (choghadiya?.slots || []).map((s) => ({ name: NA(s.name), span: fmtSpan(s.start, s.end, ct), nature: qtone(s.quality) === "good" ? "good" : "bad", note: s.period })) },
  };

  // ── Accordion sub-headlines ──
  // Today's subtitle is intentionally omitted when birth details exist (H) — the
  // Tarabalam & Chandrabalam card carries the verdict instead.
  const todaySub = prof ? null : "Add birth details to personalise";
  const rightNowSub = [curHora && pt(curHora.planet || NA(curHora.display)), curChog && pt(NA(curChog.name))].filter(Boolean).join(" · ");
  const bestForSub = bestWin ? `${bestWinName} · ${bestWin}` : "Brahma, Abhijit, Rahu Kalam & more";
  const avoidSub = (w.rahu && w.rahu.start != null) ? `Rahu Kalam · ${fmtSpan(w.rahu.start, w.rahu.end, tz)}` : "Rahu Kalam, Yamaganda, Gulika & Varjyam";
  const weekday = (dateLabel || "").split(",")[0].trim() || "this day";
  // Siddhi Yogas & Varjyam — real windows from the /lab almanac (choghadiya),
  // rendered as auspicious / avoid rows (F1). Each array item becomes its own row;
  // an empty list falls back to a single "None today" row.
  const siddhiArr = Array.isArray(choghadiya?.siddhi_yogas) ? choghadiya.siddhi_yogas.filter((s) => s && typeof s === "object" && s.name) : [];
  const varjyamArr = Array.isArray(choghadiya?.varjyam_all) && choghadiya.varjyam_all.length
    ? choghadiya.varjyam_all.filter((v) => v && typeof v === "object")
    : (choghadiya?.varjyam && typeof choghadiya.varjyam === "object" ? [choghadiya.varjyam] : []);
  const siddhiItems = siddhiArr.length
    ? siddhiArr.map((s, i) => ({
        key: `siddhi-${i}`, name: s.name, tone: "good",
        win: (s.start_unix != null && s.end_unix != null) ? { start: s.start_unix, end: s.end_unix } : null,
        note: s.significance, naksh: s.nakshatra ? pt(NA(s.nakshatra)) : null,
        amber: s.prohibition && s.prohibition !== s.significance ? s.prohibition : null,
      }))
    : [{ key: "siddhi", name: "Siddhi Yogas", win: null, tone: "good", naReason: isBirth ? "None on birth day" : "None today" }];
  const varjyamItems = varjyamArr.length
    ? varjyamArr.map((v, i) => ({
        key: `varjyam-${i}`, name: `Varjyam · ${pt(NA(v.nakshatra))}`, tone: "bad",
        win: (v.start_unix != null && v.end_unix != null) ? { start: v.start_unix, end: v.end_unix } : null,
        note: v.nature || "Toxic / Inauspicious (Tyajya Kāla)",
      }))
    : [{ key: "varjyam", name: "Varjyam", win: null, tone: "bad", naReason: isBirth ? "None on birth day" : "None today" }];
  // Surface Varjyam (Tyājya) in the collapsed Muhurta-windows subtitle so this
  // inauspicious window is visible without expanding the accordion.
  const varjyamWin = varjyamArr.find((v) => v.start_unix != null && v.end_unix != null);
  const varjyamSub = varjyamWin ? `Varjyam ${fmtSpan(varjyamWin.start_unix, varjyamWin.end_unix, tz)}` : null;

  const goodItems = [
    { key: "brahma", name: "Brahma Muhurta", win: w.brahma, tone: "good", note: "Ideal for meditation & sadhana" },
    { key: "abhijit", name: "Abhijit Muhurta", win: w.abhijit, tone: "good", note: "Victory window for key tasks", naReason: `Not applicable on ${weekday}` },
    ...siddhiItems,
  ];
  const badItems = [
    { key: "rahu", name: "Rahu Kalam", win: w.rahu, tone: "bad", note: "Avoid new beginnings" },
    { key: "yama", name: "Yamaganda", win: w.yama, tone: "bad", note: "Avoid important starts" },
    { key: "gulika", name: "Gulika Kalam", win: w.gulika, tone: "bad", note: "Inauspicious for new work" },
    ...varjyamItems,
  ];

  // Birth mode — the window(s) actually active at the exact birth instant.
  const birthItems = [...goodItems, ...badItems];
  const birthActive = isBirth
    ? birthItems.filter((x) => x.win && x.win.start != null && birthNow != null && birthNow >= x.win.start && birthNow < x.win.end)
    : [];
  const birthSub = birthActive.length ? birthActive.map((x) => x.name).join(" · ") : "No special window at birth";

  // Karmakala windows — own card (F2). Subtitle = the division running now, else next.
  const kwObj = (mode !== "birth" && choghadiya?.festivals?.karmakala_windows) || {};
  const kwList = KARMAKALA_ORDER.map((k) => kwObj[k]).filter((kw) => kw && kw.start_unix != null);
  const kwActive = kwList.find((kw) => now >= kw.start_unix && now < kw.end_unix);
  const kwNext = kwList.filter((kw) => kw.start_unix > now).sort((a, b) => a.start_unix - b.start_unix)[0];
  const hasKarmakala = kwList.length > 0;
  const karmakalaSub = kwActive ? `Now: ${kwActive.name}` : kwNext ? `Next: ${kwNext.name}` : "Scriptural day divisions";

  const todayFests = (festivals?.today || []);
  const upcomingFests = (festivals?.upcoming || []);
  const festSub = todayFests.length
    ? `Today: ${todayFests[0].name}`
    : (upcomingFests[0] ? `Next: ${upcomingFests[0].name}` : "No festival today");

  // Choghadiya block (reused inside Details)
  const choghBlock = choghadiya?.slots?.length > 0 ? (() => {
    const { day, night } = splitDayNight(choghadiya.slots);
    const cur = choghadiya.slots.find((s) => s.isCurrent);
    const nowLabel = isBirth ? "At birth" : "Now";
    const render = (s, i) => (
      <TimeCard key={i} testid={`chogh-slot-${i}`} tone={qtone(s.quality)} isCurrent={s.isCurrent} nowLabel={nowLabel}
        title={pt(NA(s.name))} sub={isDayNight(s.period) ? null : NA(s.period)} span={fmtSpan(s.start, s.end, ct)} />
    );
    return (
      <section data-testid="choghadiya">
        <button type="button" onClick={() => setOpenChog((v) => !v)} data-testid="choghadiya-toggle"
          className="mb-3 flex w-full items-center justify-between gap-2 text-left">
          <h3 className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-white/60">
            <Clock className="h-4 w-4" /> {isBirth ? "Birth Choghadiya" : <><T>Choghadiya ·</T> {NA(panchang.vara?.name)}</>}
          </h3>
          <span className="flex items-center gap-2 text-xs text-amber-300/80">
            <span className="inline-flex items-center gap-1">{openChog ? "Hide" : "Show all"}<ChevronDown className={`h-4 w-4 transition-transform ${openChog ? "rotate-180" : ""}`} /></span>
          </span>
        </button>
        {openChog ? (
          <div className="space-y-5">
            <DayNightBlock testid="choghadiya-day" label="Day Choghadiya" Icon={SunIcon} items={day} render={render} />
            <DayNightBlock testid="choghadiya-night" label="Night Choghadiya" Icon={MoonIcon} items={night} render={render} />
          </div>
        ) : (
          cur ? (
            <button type="button" data-testid="chogh-now-card" onClick={() => setOpenChog(true)}
              className="group flex w-full items-center gap-4 rounded-2xl border border-amber-400/40 bg-white/[0.04] p-4 text-left transition-colors hover:border-amber-300/70">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-amber-400/10 ring-1 ring-amber-400/25"><SunIcon className="h-6 w-6 text-amber-300" /></span>
              <div className="min-w-0 flex-1">
                <span className="rounded-full bg-amber-400 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-black">{nowLabel}</span>
                <p className="mt-1.5 font-serif-latin text-xl leading-tight text-white">{pt(NA(cur.name))}</p>
                <p className="text-sm text-white/65"><span className="dh-num">{fmtSpan(cur.start, cur.end, ct)}</span></p>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-2">
                <span className="ptile-q" data-tone={qtone(cur.quality)}>{QLABEL[qtone(cur.quality)] || "Neutral"}</span>
                <span className="inline-flex items-center gap-1 text-xs text-amber-300/80"><T>Show all</T> <ChevronDown className="h-3.5 w-3.5" /></span>
              </div>
            </button>
          ) : null
        )}
      </section>
    );
  })() : null;

  // Hora block (reused inside Details)
  const horaBlock = panchang.horas?.length > 0 ? (() => {
    const { day, night } = splitDayNight(panchang.horas);
    const curH = panchang.horas.find((h) => h.isCurrent);
    const nowLabel = isBirth ? "At birth" : "Now";
    const horaName = (h) => {
      const full = NA(h.display || h.planet);
      const parts = String(full).split("·");
      const translated = h.planet ? pt(h.planet) : null;
      return { main: translated || parts[0].trim(), rest: translated ? null : (parts.length > 1 ? parts.slice(1).join("·").trim() : null) };
    };
    const render = (h, i) => {
      const n = horaName(h);
      return (
        <TimeCard key={i} testid={`hora-slot-${i}`} tone={qtone(h.quality)} isCurrent={h.isCurrent} nowLabel={nowLabel}
          qlabel={qVerbatim(h.quality)}
          title={n.main} sub={n.rest || (isDayNight(h.phase) ? null : NA(h.phase))} span={fmtSpan(h.start, h.end, tz)} />
      );
    };
    return (
      <section data-testid="horas">
        <button type="button" onClick={() => setOpenHora((v) => !v)} data-testid="hora-toggle"
          className="mb-3 flex w-full items-center justify-between gap-2 text-left">
          <h3 className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-white/60">
            <Clock className="h-4 w-4" /> {isBirth ? "Birth Hora" : "Hora · Planetary Hours"}
          </h3>
          <span className="flex items-center gap-2 text-xs text-amber-300/80">
            <span className="inline-flex items-center gap-1">{openHora ? "Hide" : "Show all"}<ChevronDown className={`h-4 w-4 transition-transform ${openHora ? "rotate-180" : ""}`} /></span>
          </span>
        </button>
        {openHora ? (
          <div className="space-y-5">
            <DayNightBlock testid="hora-day" label="Day Hora" Icon={SunIcon} items={day} render={render} />
            <DayNightBlock testid="hora-night" label="Night Hora" Icon={MoonIcon} items={night} render={render} />
          </div>
        ) : (
          curH ? (
            <button type="button" data-testid="hora-now-card" onClick={() => setOpenHora(true)}
              className="group flex w-full items-center gap-4 rounded-2xl border border-amber-400/40 bg-white/[0.04] p-4 text-left transition-colors hover:border-amber-300/70">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-amber-400/10 ring-1 ring-amber-400/25"><Clock className="h-6 w-6 text-amber-300" /></span>
              <div className="min-w-0 flex-1">
                <span className="rounded-full bg-amber-400 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-black">{nowLabel}</span>
                <p className="mt-1.5 font-serif-latin text-xl leading-tight text-white">{horaName(curH).main}</p>
                <p className="text-sm text-white/65">{horaName(curH).rest ? <span className="text-white/50">{horaName(curH).rest} · </span> : null}<span className="dh-num">{fmtSpan(curH.start, curH.end, tz)}</span></p>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-2">
                <span className="ptile-q" data-tone={qtone(curH.quality)}>{qVerbatim(curH.quality) || QLABEL[qtone(curH.quality)] || "Neutral"}</span>
                <span className="inline-flex items-center gap-1 text-xs text-amber-300/80"><T>Show all</T> <ChevronDown className="h-3.5 w-3.5" /></span>
              </div>
            </button>
          ) : null
        )}
      </section>
    );
  })() : null;

  // ── Sacred Puja Muhurtas (festival vyaptis from /lab/choghadiya .festivals) ──
  // Evaluates Nirṇayasindhu & Vaidyanātha Dīkṣitīyam karmakala vyaptis. Active
  // observances are objects; the "none" sentinel is a plain-string entry.
  const sacredPujaBlock = mode !== "birth" ? (() => {
    const raw = choghadiya?.festivals?.special_pujas_today;
    const pujas = Array.isArray(raw) ? raw.filter((x) => x && typeof x === "object" && (x.festival || x.name)) : [];
    const tradition = (f) => {
      const src = `${f.authority || ""} ${f.rule || ""}`;
      if (/nir[nṇ]aya\s*sindhu/i.test(src)) return "Nirṇayasindhu";
      if (/vaidyan[āa]tha/i.test(src)) return "Vaidyanātha Dīkṣitīyam";
      return (f.authority || f.rule || "").trim();
    };
    return (
      <section data-testid="sacred-puja-muhurtas">
        <h3 className="mb-3 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-white/60">
          <Flame className="h-4 w-4" /> <T>Upcoming Festivals & Sacred Puja Muhurtas</T>
        </h3>
        {pujas.length === 0 ? (
          <div data-testid="sacred-puja-empty" className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-5 text-center">
            <span className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-amber-400/10 text-amber-300/80"><Flame className="h-5 w-5" /></span>
            <p className="text-sm leading-relaxed text-white/60"><T>No major festival or vrata vyāpti active for this calendar day. Regular daily muhurtas apply.</T></p>
          </div>
        ) : (
          <div className="space-y-3">
            {pujas.map((f, i) => {
              const name = f.festival || f.name;
              const win = f.muhurta_window || ((f.start_str && f.end_str) ? `${f.start_str} – ${f.end_str}` : null);
              const ruleLabel = f.muhurta || f.rule || f.authority;
              const trad = tradition(f);
              const devata = f.devata || f.deity || null;
              const guard = f.caution || f.note || f.special_note || f.vrat_guard || f.guard || null;
              return (
                <div key={i} data-testid={`sacred-puja-${i}`} className="rounded-2xl border-l-4 border-amber-600 bg-amber-50/70 p-4 dark:bg-amber-950/20">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <p className="font-serif-latin text-lg leading-tight text-amber-950 dark:text-amber-100">{name}</p>
                    {trad && <span data-testid={`sacred-puja-tradition-${i}`} className="shrink-0 rounded-full border border-amber-500/40 bg-amber-500/10 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">{trad}</span>}
                  </div>
                  {devata && <p className="mt-1 text-sm text-amber-800/90 dark:text-amber-200/80">{devata}</p>}
                  {win && (
                    <div className="mt-3">
                      <span className="inline-flex items-center gap-1.5 rounded-lg bg-amber-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm"><Clock className="h-3.5 w-3.5" /> <span className="dh-num">{win}</span></span>
                      {ruleLabel && <p data-testid={`sacred-puja-rule-${i}`} className="mt-1.5 text-xs font-medium text-amber-800/80 dark:text-amber-200/70">{ruleLabel}</p>}
                    </div>
                  )}
                  {f.significance && <p className="mt-2.5 text-sm leading-relaxed text-amber-900/80 dark:text-amber-100/70">{f.significance}</p>}
                  {guard && (
                    <div data-testid={`sacred-puja-guard-${i}`} className="mt-3 rounded-lg border border-blue-200 bg-blue-50 p-2.5 text-xs text-blue-900 dark:border-blue-900/50 dark:bg-blue-950/30 dark:text-blue-200">
                      {guard}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    );
  })() : null;

  // ── Scriptural Karmakala Windows (कर्मकाल) — interactive, live "ACTIVE NOW" ──
  const karmakalaBlock = mode !== "birth" ? (() => {
    const kw = choghadiya?.festivals?.karmakala_windows || {};
    const keys = Object.keys(kw).sort((a, b) => ((KARMAKALA_ORDER.indexOf(a) + 1 || 99) - (KARMAKALA_ORDER.indexOf(b) + 1 || 99)));
    if (keys.length === 0) return null;
    return (
      <section data-testid="karmakala-windows">
        <h3 className="mb-3 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-white/60">
          <Scroll className="h-4 w-4" /> <T>Scriptural Karmakala Windows · कर्मकाल</T>
        </h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {keys.map((k) => {
            const w = kw[k] || {};
            const prominent = KARMAKALA_PROMINENT.has(k);
            const active = w.start_unix != null && w.end_unix != null && now >= w.start_unix && now < w.end_unix;
            const en = KARMAKALA_EN[k];
            return (
              <div key={k} data-testid={`karmakala-${k}`}
                className={`rounded-2xl border p-4 transition-colors ${prominent ? "border-amber-400/40 bg-gradient-to-br from-amber-400/[0.08] to-transparent" : "border-white/[0.08] bg-white/[0.03]"} ${active ? "ring-1 ring-emerald-400/60" : ""}`}>
                <div className="flex items-start justify-between gap-2">
                  <p className="font-serif-latin text-base leading-tight text-white">{pt(NA(w.name || k))}{en ? <span className="text-sm text-white/50"> ({en})</span> : null}</p>
                  {active && (
                    <span data-testid={`karmakala-active-${k}`} className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-emerald-400/60 bg-emerald-500/15 px-2.5 py-1 text-[9px] font-bold uppercase tracking-wide text-emerald-300 shadow-[0_0_14px_rgba(16,185,129,0.45)]">
                      <span className="relative flex h-1.5 w-1.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" /></span>
                      <T>Active Now</T>
                    </span>
                  )}
                </div>
                <p className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-amber-500/15 px-2.5 py-1 text-sm font-medium text-amber-200 tabular-nums"><Clock className="h-3.5 w-3.5" /> <span className="dh-num">{w.start_str} – {w.end_str}</span></p>
                {w.significance && <p className="mt-2 text-sm leading-relaxed text-white/60">{w.significance}</p>}
              </div>
            );
          })}
        </div>
      </section>
    );
  })() : null;


  return (
    <div className="space-y-3.5" data-testid="premium-panchang">
      {/* ── GREETING — always first ─────────────────────────────────── */}
      <section data-testid="premium-greeting"
        className="relative overflow-hidden rounded-[1.75rem] border border-amber-400/15 bg-white/[0.03] p-6 shadow-[0_24px_60px_-40px_rgba(0,0,0,0.7)]">
        <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-amber-300/70">{mode === "birth" ? "Janma Panchangam" : "Namaskaram"}</p>
        <h2 className="mt-1.5 font-serif-latin text-[2rem] leading-tight text-white">{mode === "birth" ? "At your birth" : `Namaskaram${dispName ? `, ${dispName}` : ""}`}</h2>
        <p className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-white/60">
          <span>{dateLabel}</span>
        </p>
        {/* One share entry for the day's Panchangam card (per-card share buttons were removed on request) */}
        {mode !== "birth" && (
          <div className="mt-4" data-testid="premium-share">
            <PanchangShareImage data={shareData} premium variant="summary" plain className={shareBtnCls} />
          </div>
        )}
      </section>

      {/* ── TODAY, FOR YOU (daily only) ─────────────────────────────── */}
      {mode !== "birth" && (
        <AccSection testid="acc-today" title={<T>Today, for you</T>} sub={todaySub} Icon={Sparkles}
          open={openSec === "today"} onToggle={() => toggleSec("today")}>
          <div className="space-y-5" data-testid="today-quickfacts-wrap">
            {/* Today's panchang quick facts — Nakshatra / Tithi (date removed) */}
            <div className="grid grid-cols-2 gap-3" data-testid="today-quickfacts">
              <div data-testid="today-nakshatra"><p className="text-[10px] uppercase tracking-[0.14em] text-white/40"><T>Nakshatra</T></p><p className="mt-0.5 text-[15px] leading-tight text-white">{pt(NA(panchang.nakshatra?.name))}</p></div>
              <div data-testid="today-tithi"><p className="text-[10px] uppercase tracking-[0.14em] text-white/40"><T>Tithi</T></p><p className="mt-0.5 text-[15px] leading-tight text-white">{pt(NA(panchang.tithi?.name))}</p>{paksha && <p className="text-[11px] text-amber-200/70">{pt(paksha)}</p>}</div>
            </div>
          </div>
          {prof ? (
            <div className="mt-5 space-y-5" data-testid="premium-personal-hero">
              {/* Tara Bala & Chandrabala — single shared card (H) */}
              {tara && (tara.tara || tara.chandrabala) && (
                <div data-testid="tara-bala">
                  {tara.chandrabala?.chandrashtama && (
                    <div className="mb-3 flex items-start gap-3 rounded-2xl border border-rose-500/50 bg-rose-500/15 p-4 text-rose-200" data-testid="chandrashtama-alert">
                      <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
                      <div>
                        <p className="text-sm font-semibold"><T>Chandrashtama active</T></p>
                        <p className="mt-0.5 text-xs opacity-90">{fmtSpan(tara.chandrabala.start, tara.chandrabala.end, tz)} <T>— a sensitive window for the Moon; plan gently.</T></p>
                      </div>
                    </div>
                  )}
                  <TaraChandraCard tara={tara} tz={tz} refDate={astro.sunrise} />
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-2xl border border-amber-400/20 bg-amber-400/[0.04] p-5 text-center" data-testid="today-add-birth">
              <span className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-amber-400/15 text-amber-300"><UserPlus className="h-6 w-6" /></span>
              <p className="text-sm text-white/75"><T>Add your birth details to see your personal</T> <span className="text-amber-200"><T>Tara Bala</T></span>, <span className="text-amber-200"><T>Chandrabala</T></span> <T>and best window for the day.</T></p>
              {onAddBirth && <button onClick={onAddBirth} data-testid="today-add-birth-btn" className="mt-4 rounded-full bg-amber-400 px-5 py-2 text-sm font-semibold text-black"><T>Add birth details</T></button>}
            </div>
          )}
        </AccSection>
      )}

      {/* ── RIGHT NOW (daily only) ──────────────────────────────────── */}
      {mode !== "birth" && (curHora || curChog) && (() => {
        const hEnd = curHora?.end, cEnd = curChog?.end;
        const soonest = [hEnd, cEnd].filter((v) => v != null).sort((a, b) => a - b)[0];
        return (
          <AccSection testid="acc-rightnow" title={<T>Right now</T>} sub={rightNowSub} Icon={Clock}
            open={openSec === "rightnow"} onToggle={() => toggleSec("rightnow")}>
            <div data-testid="premium-right-now">
              <div className="grid gap-4 sm:grid-cols-2">
                {curHora && (
                  <button type="button" data-testid="right-now-hora" onClick={() => setDetail({ title: `Hora · ${pt(curHora.planet || NA(curHora.display))}`, tone: qtone(curHora.quality), subtitle: fmtSpan(curHora.start, curHora.end, tz), rows: [{ label: "Lord", value: pt(curHora.planet || NA(curHora.display)) }, { label: "Ends", value: fmtTime(curHora.end, tz) }, { label: "Window", value: fmtSpan(curHora.start, curHora.end, tz) }] })} className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-4 text-left">
                    <p className="text-[11px] uppercase tracking-[0.14em] text-white/40"><T>Hora (planetary hour)</T></p>
                    <p className="mt-1 font-serif-latin text-2xl leading-tight text-white">{pt(curHora.planet || NA(curHora.display))}</p>
                    {(curHora.label || curHora.quality) && <p className="text-[11px] text-white/50">{pt(curHora.label || curHora.quality)}</p>}
                    <p className="mt-1 text-sm text-white/70"><T>Ends</T> <span className="dh-num text-amber-200">{fmtTime(curHora.end, tz)}</span></p>
                  </button>
                )}
                {curChog && (
                  <button type="button" data-testid="right-now-chog" onClick={() => setDetail({ title: `Choghadiya · ${pt(NA(curChog.name))}`, tone: qtone(curChog.quality), subtitle: fmtSpan(curChog.start, curChog.end, ct), rows: [{ label: "Nature", value: pt(NA(curChog.name)) }, { label: "Ends", value: fmtTime(curChog.end, ct) }, { label: "Window", value: fmtSpan(curChog.start, curChog.end, ct) }] })} className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-4 text-left">
                    <p className="text-[11px] uppercase tracking-[0.14em] text-white/40"><T>Choghadiya</T></p>
                    <p className="mt-1 font-serif-latin text-2xl text-white">{pt(NA(curChog.name))}</p>
                    <p className="text-[11px] text-white/50"><PL>{qtone(curChog.quality) === "good" ? "Auspicious" : qtone(curChog.quality) === "bad" ? "Inauspicious" : "Mixed"}</PL></p>
                    <p className="mt-1 text-sm text-white/70"><T>Ends</T> <span className="dh-num text-amber-200">{fmtTime(curChog.end, ct)}</span></p>
                  </button>
                )}
              </div>
            </div>
          </AccSection>
        );
      })()}

      {/* ── PANCHANGAM DETAILS ──────────────────────────────────────── */}
      <AccSection testid="acc-details" title={<T>Panchangam details</T>} Icon={Layers}
        open={openSec === "details"} onToggle={() => toggleSec("details")}>
        <div className="space-y-7">
          {/* Sun card — Sun Rashi + sun-path (sunrise/sunset) */}
          <div className="relative overflow-hidden rounded-[1.75rem] border border-[#E0A23C]/15 bg-gradient-to-b from-[#E0A23C]/[0.06] to-transparent p-5" data-testid="premium-today-hero">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-[#E0A23C]/12 ring-1 ring-[#E0A23C]/25"><SunIcon className="celestial-pulse h-6 w-6 text-[#F4A24C]" /></span>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#E8B874]/80"><T>Sun</T></p>
                  <h2 className="mt-0.5 font-serif-latin text-2xl leading-tight text-white">{pt(NA(panchang.rashi?.sun))}</h2>
                </div>
              </div>
            </div>
            <SunArc sunrise={astro.sunrise} sunset={astro.sunset} moonrise={astro.moonrise} moonset={astro.moonset} tz={tz} selectedSec={selectedSec} />
          </div>

          {/* Dedicated Moon card (mirrors the Sun hero) */}
          <MoonCard panchang={panchang} astro={astro} tz={tz} selectedSec={selectedSec} pt={pt} />

          {/* Five Limbs */}
          <section data-testid="five-limbs">
            <h3 className="pd-ink3 mb-3 text-[11px] font-semibold uppercase tracking-[0.2em]"><T>The Five Limbs</T></h3>
            <div className="mb-2"><LimbCard label="Tithi" limb={panchang.tithi} tz={tz} refDate={astro.sunrise} Icon={MoonIcon} onOpen={setDetail} pt={pt} /></div>
            <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
              <LimbCard label="Vara" limb={panchang.vara} tz={tz} refDate={astro.sunrise} Icon={CalendarDays} onOpen={setDetail} pt={pt} />
              <LimbCard label="Nakshatra" limb={panchang.nakshatra} tz={tz} refDate={astro.sunrise} Icon={Star} onOpen={setDetail} pt={pt} />
              <LimbCard label="Yoga" limb={panchang.yoga} tz={tz} refDate={astro.sunrise} Icon={Hourglass} onOpen={setDetail} pt={pt} />
              <LimbCard label="Karana" limb={panchang.karana} tz={tz} refDate={astro.sunrise} Icon={Layers} onOpen={setDetail} pt={pt} />
            </div>
          </section>

          {sacredPujaBlock}
          {horaBlock}
          {choghBlock}

          {/* Calendar & sky (daily) — placed last so Today mirrors the Birth order */}
          {mode !== "birth" && (
          <div className="pd-surf rounded-2xl p-5" data-testid="calendar-panel">
            <p className="pd-ink3 mb-4 text-[11px] font-semibold uppercase tracking-[0.2em]"><T>Calendar & sky</T></p>
            <div className="grid grid-cols-2 gap-x-5 gap-y-5 sm:grid-cols-3">
              <div><p className="pd-ink3 text-[10px] font-medium uppercase tracking-wide"><T>Vikram Samvat</T></p><p className="pd-ink mt-1 text-[15px] font-semibold tabular-nums">{NA(sv.vikram)}</p></div>
              <div><p className="pd-ink3 text-[10px] font-medium uppercase tracking-wide"><T>Shaka Samvat</T></p><p className="pd-ink mt-1 text-[15px] font-semibold tabular-nums">{NA(sv.shaka)}</p></div>
              <div><p className="pd-ink3 text-[10px] font-medium uppercase tracking-wide"><T>Samvatsara</T></p><p className="pd-ink mt-1 text-[15px] font-semibold">{pt(NA((sv.sauramana || "").replace(/^SS/, "")))}</p></div>
              <div><p className="pd-ink3 text-[10px] font-medium uppercase tracking-wide"><T>Ritu</T></p><p className="pd-ink mt-1 text-[15px] font-semibold">{pt(NA(sv.ritu))}</p></div>
              <div><p className="pd-ink3 text-[10px] font-medium uppercase tracking-wide"><T>Masa</T></p><p className="pd-ink mt-1 text-[15px] font-semibold">{pt(NA(sv.amanta))}</p><p className="pd-ink3 text-[11px]"><T>Amanta</T></p></div>
              {astro.ayanamsa != null && <div><p className="pd-ink3 text-[10px] font-medium uppercase tracking-wide"><T>Ayanamsa</T></p><p className="pd-ink mt-1 text-[15px] font-semibold tabular-nums">{astro.ayanamsa}°</p></div>}
            </div>
          </div>
          )}
        </div>
      </AccSection>

      {/* ── MUHURTA WINDOWS (birth) — after details, window active at birth ── */}
      {mode === "birth" && (
        <AccSection testid="acc-windows" title={<T>Muhurta windows</T>} sub={varjyamSub ? `${birthSub} · ${varjyamSub}` : birthSub} Icon={SunLucide}
          open={openSec === "windows"} onToggle={() => toggleSec("windows")}>
          <AuspiciousWindows tz={tz} sunrise={astro.sunrise} sunset={astro.sunset} items={birthItems}
            nowOverride={birthNow} atLabel="At birth" markerLabel="birth" birthMode />
        </AccSection>
      )}

      {/* ── MUHURTA WINDOWS (daily) — below details so limbs sit higher ── */}
      {mode !== "birth" && (
        <AccSection testid="acc-windows" title={<T>Muhurta windows</T>} sub={varjyamSub ? `${bestForSub} · ${varjyamSub}` : bestForSub} Icon={SunLucide}
          open={openSec === "windows"} onToggle={() => toggleSec("windows")}>
          <AuspiciousWindows tz={tz} sunrise={astro.sunrise} sunset={astro.sunset} items={[...goodItems, ...badItems]} />
        </AccSection>
      )}

      {/* ── KARMAKALA WINDOWS (daily) — own card, collapsed by default ── */}
      {mode !== "birth" && hasKarmakala && (
        <AccSection testid="acc-karmakala" title={<T>Karmakala windows</T>} sub={karmakalaSub} Icon={Scroll}
          open={openSec === "karmakala"} onToggle={() => toggleSec("karmakala")}>
          {karmakalaBlock}
        </AccSection>
      )}

      {/* ── FESTIVALS & VRATS (daily only, if data) ─────────────────── */}
      {mode !== "birth" && festivals && (
        <AccSection testid="acc-festivals" title={<T>Festivals & Vrats</T>} sub={festSub} Icon={Flame}
          open={openSec === "festivals"} onToggle={() => toggleSec("festivals")}>
          {todayFests.length > 0 && (
            <div className="mb-4" data-testid="festivals-today">
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-amber-300/70"><T>Today</T></p>
              <div className="space-y-2">
                {todayFests.map((f, i) => <FestivalRow key={`t${i}`} f={f} highlight /> )}
              </div>
            </div>
          )}
          {upcomingFests.length > 0 ? (
            <div data-testid="festivals-upcoming">
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/40"><T>Upcoming</T></p>
              <div className="space-y-2">
                {upcomingFests.map((f, i) => <FestivalRow key={`u${i}`} f={f} />)}
              </div>
            </div>
          ) : (!todayFests.length && (
            <p className="py-2 text-sm text-white/50" data-testid="festivals-empty"><T>No festivals or vrats around this date.</T></p>
          ))}
          <CivilTithiNote dark className="mt-4" />
        </AccSection>
      )}

      <DetailSheet detail={detail} onClose={() => setDetail(null)} />
    </div>
  );
}

// A festival/vrat row — name, Devanagari, date and short significance.
function FestivalRow({ f, highlight }) {
  const dstr = (() => {
    try { const [y, m, d] = String(f.date).split("-").map(Number); return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-GB", { timeZone: "UTC", weekday: "short", day: "numeric", month: "short" }); }
    catch { return f.date; }
  })();
  return (
    <div data-testid="festival-row" className={`flex items-start gap-3 rounded-2xl border p-3.5 ${highlight ? "border-amber-400/35 bg-amber-400/[0.06]" : "border-white/[0.08] bg-white/[0.03]"}`}>
      <span className={`mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${highlight ? "bg-amber-400/15 text-amber-300" : "bg-white/5 text-white/50"}`}>
        <Flame className="h-[18px] w-[18px]" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <p className="font-serif-latin text-base leading-tight text-white">{f.name} {f.deva && <span className="font-deva text-sm text-amber-200/80">· {f.deva}</span>}</p>
          <span className="shrink-0 text-xs text-white/50">{dstr}</span>
        </div>
        {f.significance && <p className="mt-0.5 text-xs leading-relaxed text-white/60">{f.significance}</p>}
      </div>
    </div>
  );
}

// Seconds-since-local-midnight for an epoch, read in the given tz. This keeps
// the day/night decision tz-safe (no UTC-vs-local epoch confusion) and lets the
// arc reflect any selected date — not just the viewer's wall clock.
function secOfDay(epoch, tz) {
  if (epoch == null) return null;
  try {
    const parts = new Intl.DateTimeFormat("en-GB", { timeZone: tz, hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }).formatToParts(new Date(epoch * 1000));
    const g = (t) => Number(parts.find((x) => x.type === t)?.value || 0);
    let h = g("hour"); if (h === 24) h = 0;
    return h * 3600 + g("minute") * 60 + g("second");
  } catch { return null; }
}

// Sun/Moon-path arc hero. During the day the Sun travels the arc between
// sunrise & sunset; at night the Sun is hidden (below the horizon) and the Moon
// rides its own arc between moonrise & moonset when it is above the horizon.
// `selectedSec` (seconds-since-midnight of the chosen moment) drives the arc
// when the user is viewing a specific date/time; otherwise it tracks live.
function SunArc({ sunrise, sunset, moonrise, moonset, tz, selectedSec = null }) {
  const [now, setNow] = useState(() => Date.now() / 1000);
  useEffect(() => {
    if (selectedSec != null) return; // static moment — no live ticking
    if (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const id = setInterval(() => setNow(Date.now() / 1000), 30000);
    return () => clearInterval(id);
  }, [selectedSec]);
  if (sunrise == null || sunset == null) return null;

  const sunriseSec = secOfDay(sunrise, tz);
  const sunsetSec = secOfDay(sunset, tz);
  const ref = selectedSec != null ? selectedSec : secOfDay(now, tz);
  if (sunriseSec == null || sunsetSec == null || ref == null) return null;

  // Position on the semicircle arc for a 0→1 progress value.
  const dot = (p) => {
    const t = Math.min(1, Math.max(0, p));
    const theta = Math.PI * (1 - t);
    return { x: 150 + 130 * Math.cos(theta), y: 100 - 80 * Math.sin(theta) };
  };

  const sunUp = ref >= sunriseSec && ref <= sunsetSec;
  const sunP = (ref - sunriseSec) / Math.max(1, sunsetSec - sunriseSec);
  const sPos = dot(sunP);

  // Moon above the horizon when the reference moment is within [moonrise, moonset].
  let moonUp = false, mPos = null;
  const moonriseSec = secOfDay(moonrise, tz);
  const moonsetSec = secOfDay(moonset, tz);
  if (moonriseSec != null && moonsetSec != null) {
    if (moonsetSec > moonriseSec) { moonUp = ref >= moonriseSec && ref <= moonsetSec; }
    else { moonUp = ref >= moonriseSec || ref <= moonsetSec; } // moonset next day
    if (moonUp) {
      const span = (moonsetSec > moonriseSec ? moonsetSec - moonriseSec : (86400 - moonriseSec) + moonsetSec);
      const elapsed = (ref >= moonriseSec ? ref - moonriseSec : (86400 - moonriseSec) + ref);
      mPos = dot(elapsed / Math.max(1, span));
    }
  }

  return (
    <div className="mt-5" data-testid="sun-arc" data-phase={sunUp ? "day" : "night"}>
      <svg viewBox="0 0 300 118" className="mx-auto block w-full max-w-md" role="img" aria-label="Sun and Moon path">
        <path d="M 20 100 A 130 80 0 0 1 280 100" fill="none" stroke="rgba(228,162,60,0.35)" strokeWidth="2" strokeDasharray="4 6" />
        <line x1="12" y1="100" x2="288" y2="100" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
        {sunUp && (
          <g data-testid="sun-arc-sun" className="celestial-pulse">
            <circle cx={sPos.x} cy={sPos.y} r="18" fill="#F4A24C" opacity="0.18" />
            <circle cx={sPos.x} cy={sPos.y} r="9" fill="#F4A24C" />
            {Array.from({ length: 7 }).map((_, i) => {
              const a = (i / 7) * Math.PI * 2;
              return <line key={i} x1={sPos.x + Math.cos(a) * 12} y1={sPos.y + Math.sin(a) * 12} x2={sPos.x + Math.cos(a) * 16} y2={sPos.y + Math.sin(a) * 16} stroke="#F4A24C" strokeWidth="1.6" strokeLinecap="round" />;
            })}
          </g>
        )}
        {!sunUp && (
          // Sun below the horizon: a dimmed sun resting on the horizon line. The
          // Moon is shown only in its own Moon card, never in the Sun card.
          <g data-testid="sun-arc-rest" opacity="0.5">
            <circle cx="150" cy="104" r="7" fill="#B07A3A" />
          </g>
        )}
      </svg>
      <div className="-mt-2">
        {!sunUp && <p className="mb-2 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-white/35"><PL>Sun below horizon</PL></p>}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2"><SunriseIcon className="h-6 w-6 text-[#F4A24C]" /><div><Label><T>Sunrise</T></Label><p className="text-sm text-white/90">{fmtTime(sunrise, tz)}</p></div></div>
          <div className="flex items-center gap-2 text-right"><div><Label><T>Sunset</T></Label><p className="text-sm text-white/90">{fmtTime(sunset, tz)}</p></div><SunsetIcon className="h-6 w-6 text-[#E8705A]" /></div>
        </div>
      </div>
    </div>
  );
}


// Moon-path arc hero (mirrors SunArc). The Moon rides the arc between moonrise
// and moonset; when below the horizon the arc shows a dimmed resting moon.
// `selectedSec` drives the position for a chosen moment, else it tracks live.
function MoonArc({ moonrise, moonset, tz, selectedSec = null, phase = null }) {
  const [now, setNow] = useState(() => Date.now() / 1000);
  useEffect(() => {
    if (selectedSec != null) return;
    if (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const id = setInterval(() => setNow(Date.now() / 1000), 30000);
    return () => clearInterval(id);
  }, [selectedSec]);
  if (moonrise == null || moonset == null) return null;

  const mrSec = secOfDay(moonrise, tz);
  const msSec = secOfDay(moonset, tz);
  const ref = selectedSec != null ? selectedSec : secOfDay(now, tz);
  if (mrSec == null || msSec == null || ref == null) return null;

  const dot = (p) => {
    const t = Math.min(1, Math.max(0, p));
    const theta = Math.PI * (1 - t);
    return { x: 150 + 130 * Math.cos(theta), y: 100 - 80 * Math.sin(theta) };
  };

  let moonUp = false, span = 1, elapsed = 0;
  if (msSec > mrSec) { moonUp = ref >= mrSec && ref <= msSec; span = msSec - mrSec; elapsed = ref - mrSec; }
  else { moonUp = ref >= mrSec || ref <= msSec; span = (86400 - mrSec) + msSec; elapsed = ref >= mrSec ? ref - mrSec : (86400 - mrSec) + ref; }
  const mPos = dot(elapsed / Math.max(1, span));

  return (
    <div className="mt-5" data-testid="moon-arc" data-phase={moonUp ? "up" : "down"}>
      <svg viewBox="0 0 300 118" className="mx-auto block w-full max-w-md" role="img" aria-label="Moon path">
        <path d="M 20 100 A 130 80 0 0 1 280 100" fill="none" stroke="rgba(143,183,232,0.35)" strokeWidth="2" strokeDasharray="4 6" />
        <line x1="12" y1="100" x2="288" y2="100" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
        {moonUp ? (
          <g data-testid="moon-arc-moon" className="celestial-pulse">
            <circle cx={mPos.x} cy={mPos.y} r="16" fill="#8FB7E8" opacity="0.16" />
            {phase != null ? (
              <foreignObject x={mPos.x - 11} y={mPos.y - 11} width="22" height="22">
                <MoonPhase phase={phase} size={22} />
              </foreignObject>
            ) : (
              <>
                <circle cx={mPos.x} cy={mPos.y} r="8.5" fill="#C9DAF2" />
                <circle cx={mPos.x + 3.5} cy={mPos.y - 1} r="7.5" fill="#1a2234" />
              </>
            )}
          </g>
        ) : (
          <g data-testid="moon-arc-rest" opacity="0.55">
            <circle cx="150" cy="104" r="7" fill="#6E8BB0" />
            <circle cx="152.6" cy="103.2" r="6.2" fill="#1a2234" />
          </g>
        )}
      </svg>
      <div className="-mt-2">
        <p className="mb-2 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-white/35"><PL>{moonUp ? "Above horizon" : "Below horizon"}</PL></p>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2"><MoonriseIcon className="h-6 w-6 text-[#8FB7E8]" /><div><Label><T>Moonrise</T></Label><p className="text-sm text-white/90 tabular-nums">{fmtTime(moonrise, tz)}</p></div></div>
          <div className="flex items-center gap-2 text-right"><div><Label><T>Moonset</T></Label><p className="text-sm text-white/90 tabular-nums">{fmtTime(moonset, tz)}</p></div><MoonsetIcon className="h-6 w-6 text-[#6E8BB0]" /></div>
        </div>
      </div>
    </div>
  );
}